/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColDef } from '@ag-grid-community/core';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AgFns,
  BaseListEditFrmComponent,
  ExtraGridOptions,
  GridOptionsExt,
  ISortModel,
  ProxAgFns,
  StatusChangeDialog,
  StatusService,
} from '@core';
import { Account, AccountPurchaseOrder, ActiveStatus, ActiveStatusEnum, Program } from '@models';
import { BaseService } from '@core';

import { DateFns, UtilFns } from '@utils';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';
import * as _ from 'lodash';
import { Guid } from 'guid-typescript';
import { AccountPurchaseOrderFinderDialog } from './account-purchase-order-finder.dialog';
import { EntityState } from 'breeze-client';
import { PurchaseOrderAllocateDialogComponent } from './purchase-order-allocate.dialog.component';
import { DialogService } from 'primeng/dynamicdialog';
import { AccountPurchaseOrderLogFinderDialog } from './account-purchase-order-log-finder.dialog';

@Component({
  selector: 'prox-account-purchase-orders',
  templateUrl: 'account-purchase-orders.component.html',
  standalone: false,
})
// export class AccountPurchaseOrdersComponent extends BaseListEditFrmComponent<AccountPurchaseOrder> {
export class AccountPurchaseOrdersComponent extends BaseListEditFrmComponent<AccountPurchaseOrder> {
  accountId!: string;
  account!: Account;
  parentPos: AccountPurchaseOrder[] = [];
  isAddingParent = false;
  activeStatuses: ActiveStatus[] = [];
  showInactive = false;
  displayedEntities: AccountPurchaseOrder[] = [];

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    public pngDialogService: DialogService,
    private statusService: StatusService,
    override dbSaveService: AccountDbSaveService,
    override dbQueryService: AccountDbQueryService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  override async updateFromParams(params: object): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');
    this.account = await this.dbQueryService.getAccountById(this.accountId);
    UtilFns.assertNonNull(this.account, 'Account');
    const accPos = await this.dbQueryService.getAccountPurchaseOrders(this.accountId);
    this.parentPos = accPos.filter((x) => x.parentAccountPurchaseOrderId == x.id);
    this.parentPos.unshift({ id: undefined as any as string, purchaseOrder: '- None -' } as AccountPurchaseOrder);

    this.activeStatuses = await this.dbQueryService.getAll(ActiveStatus);

    this.initialize(AccountPurchaseOrder, 'Purchase Orders', accPos);
    this.setTitle('Purchase Orders for ' + this.account.name);
    this.gridOptions.onFirstDataRendered = this.updateColVisibility.bind(this);
    this.refreshDisplayedEntities();
    this.isPageReady = true;
  }

  refreshDisplayedEntities() {
    // "Show Inactive?" hides ONLY Inactive rows; Hold and Phaseout remain visible so admins
    // can still see (and re-Activate) recently held items without needing to also opt into
    // Inactive noise.
    this.displayedEntities = this.showInactive
      ? this.entities
      : this.entities.filter((e) => e.activeStatusId !== ActiveStatusEnum.Inactive);
  }

  onShowInactiveChange() {
    this.refreshDisplayedEntities();
  }

  override getColDefs() {
    const columnDefs: ColDef[] = [
      {
        headerName: 'Purchase Order',
        field: 'purchaseOrder',
        editable: !this.isReadOnly(),
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          innerRenderer: (params: any) => {
            const po = params.data as AccountPurchaseOrder;
            const name = params.value ?? '';
            if (po && !po.isParent() && po.parentAccountPurchaseOrderId != null) {
              return `<i class="pi pi-sitemap text-gray-500 mr-1" style="font-size: 0.75rem;" title="Child of ${po.parentAccountPurchaseOrder?.purchaseOrder ?? 'parent PO'}"></i>${name}`;
            }
            return name;
          },
        },
        ...AgFns.flexWidth(300),
      },
      {
        headerName: 'Start Date',
        field: 'startDate',
        editable: !this.isReadOnly(),
        cellEditor: 'agDateCellEditor',
        filter: 'agDateColumnFilter',
        ...AgFns.flexWidth(180),
      },
      {
        headerName: 'End Date',
        field: 'endDate',
        editable: !this.isReadOnly(),
        cellEditor: 'agDateCellEditor',
        filter: 'agDateColumnFilter',
        ...AgFns.flexWidth(180),
      },
      {
        headerName: 'Amount',
        field: 'amt',
        editable: this.isAmtEditable.bind(this),
        filter: 'agNumberColumnFilter',
        ...AgFns.flexWidth(150),
        onCellValueChanged: (params) => {
          const po = params.data as AccountPurchaseOrder;
          if (po == null) return;
          if (po.isParent() || po.parentAccountPurchaseOrderId != null) {
            this.dbSaveService.getChanges().forEach((e) => e.entityAspect.validateEntity());
            this.gridOptions.apiX?.redrawRows();
          }
        },
      },
      // NOTE: DO NOT UNCOMMENT THIS before talking to Jay.  It is NOT needed.  All allocations are handled by
      // directly editing the parent or child PO's.  And a full history of the changes shows in the logs.
      // {
      //   ...AgFns.createButtonProps('', this.onAllocate.bind(this), { calcLabel: this.calcAllocateLabel.bind(this) }),
      //   maxWidth: 110,
      //   colId: 'allocate',
      // },
      {
        headerName: 'Amount Used',
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => {
          const po = params.data as AccountPurchaseOrder;
          if (po == null) return;
          return po.getAmtUsed();
        },
        colId: 'usedAmt', // HACK: colId endsWith amt triggers $ formatting.
        ...AgFns.flexWidth(150),
      },
      { headerName: 'Warning at x% remaining', field: 'warnIfWithinRate', editable: !this.isReadOnly(), ...AgFns.flexWidth(250) },
      {
        ...AgFns.createButtonProps('Parent Purchase Order', this.onViewParent.bind(this), { calcLabel: this.calcParentLabel.bind(this) }),
        ...AgFns.flexWidth(150),
        colId: 'parentPo',
      },
      {
        headerName: 'Amount Allocated',
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => {
          const po = params.data as AccountPurchaseOrder;
          if (!po.isParent()) return;
          return po.getAmtAllocated();
        },
        colId: 'allocAmt', // HACK: colId endsWith amt triggers $ formatting.
        ...AgFns.flexWidth(150),
      },
      {
        hide: true,
        valueGetter: (params) => {
          const po = params.data as AccountPurchaseOrder;
          if (po == null) return;

          return (po.isParent() ? po.purchaseOrder : '-') + po.purchaseOrder;
        },
        colId: 'sort',
      },
      { ...AgFns.createButtonProps('Show History', this.onShowApoLogs.bind(this), { label: 'Show History' }), ...AgFns.fixWidth(180) },
      { ...AgFns.createButtonProps('Show Change Logs', this.onShowLog.bind(this), { label: 'Show Change Log' }), ...AgFns.fixWidth(180) },
      { ...ProxAgFns.getActiveStatusColDef('activeStatus.name'), ...AgFns.fixWidth('status') },
      ProxAgFns.getEntityStatusColDef(this.onChangeActiveStatus.bind(this), { canDisplay: this.canEdit.bind(this) }),
      ProxAgFns.getEntityDeleteColDef(this.onDelete.bind(this), { canDisplay: this.canDelete.bind(this) }),
    ];
    return columnDefs;
  }

  async onChangeActiveStatus(row: AccountPurchaseOrder) {
    await this.statusService.onChangeActiveStatus(this, row, 'Account Purchase Order');
    this.refreshDisplayedEntities();
    this.gridOptions.apiX?.refreshCells();
  }

  async onShowApoLogs(po: AccountPurchaseOrder) {
    const logs = await this.dbQueryService.getAccountPurchaseOrderLogsWithDetails(po.id);
    const r = await this.dialogService.createFinder(AccountPurchaseOrderLogFinderDialog, {
      accountPurchaseOrderLogs: logs,
    });
  }

  onShowLog(po: AccountPurchaseOrder) {
    return this.dialogService.changeLogDialog(this.dbSaveService.uow, null, po, po.id);
  }

  async onAllocate(parentPuchaseOrder: AccountPurchaseOrder) {
    await PurchaseOrderAllocateDialogComponent.open(this.pngDialogService, {
      parentPuchaseOrder: parentPuchaseOrder,
    });
  }

  // calcAllocateLabel(po: AccountPurchaseOrder) {
  //   if (po.isParent()) {
  //     return 'Allocate';
  //   } else {
  //     return '';
  //   }
  // }

  override getSortModel(): ISortModel {
    return [
      { colId: 'sort', sort: 'desc' },
      // { colId: 'startDate', sort: 'desc' },
    ];
  }

  override getExtraGridOptions(): ExtraGridOptions {
    return { detailProperty: 'childAccountPurchaseOrders' };
  }

  override updateMasterDetail(parentGridOptions: GridOptionsExt) {
    parentGridOptions.detailRowAutoHeight = true;
    parentGridOptions.masterDetail = true;
    const detailGridOptions = AgFns.createDetailGridOptions();
    detailGridOptions.columnDefs = [{ headerName: 'Child Purchase Orders', field: 'purchaseOrder', editable: !this.isReadOnly() }];
    AgFns.updateColDefs(detailGridOptions.columnDefs);
    parentGridOptions.detailCellRendererParams = {
      detailGridOptions: detailGridOptions,
      refreshStrategy: 'everything',
      getDetailRowData: (params) => {
        const apo = params.data as AccountPurchaseOrder;
        const r = apo.childAccountPurchaseOrders.filter((x) => x.parentAccountPurchaseOrderId != x.id);
        params.successCallback(r);
      },
    };
  }

  updateColVisibility() {
    if (this.gridOptions.apiX == null) return;
    const hasAnyParents = this.entities.some((x) => x.isParent());
    this.gridOptions.apiX.setColumnsVisible(['parentPo'], hasAnyParents);
    this.gridOptions.apiX.setColumnsVisible(['allocAmt'], hasAnyParents);
  }

  isAmtEditable(params: any) {
    if (this.isReadOnly()) return false;
    const po = params.data as AccountPurchaseOrder;
    if (po.isParent()) {
      return true;
    } else {
      if (po.entityAspect.entityState == EntityState.Added) {
        return true;
      }
      return po.parentAccountPurchaseOrderId != null;
    }
  }

  calcParentLabel(po: AccountPurchaseOrder) {
    if (po.parentAccountPurchaseOrderId == po.id) {
      return '';
    } else {
      return po.parentAccountPurchaseOrder?.purchaseOrder ?? 'None';
    }
  }

  async onViewParent(po: AccountPurchaseOrder) {
    const isParent = po.id == po.parentAccountPurchaseOrderId;
    if (!isParent) {
      if (this.isReadOnly()) return;
      const r = await this.dialogService.createFinder(AccountPurchaseOrderFinderDialog, { accountPurchaseOrders: this.parentPos });
      if (r.length > 0) {
        po.parentAccountPurchaseOrderId = r[0].id;

        this.dbSaveService.getChanges().forEach((e) => e.entityAspect.validateEntity());
        this.gridOptions.apiX?.redrawRows();
        // AgFns.refreshGrid(this.gridOptions, this.entities);
      }
    } else {
      // do nothing
    }
  }

  isReadOnly() {
    return this.statusService.getWorkingStatus(this.account).isReadOnly;
  }

  canDelete(po: AccountPurchaseOrder) {
    if (po.isParent()) {
      // parent always has itself as child
      return po.childAccountPurchaseOrders.length == 1;
    }
    return !this.isReadOnly();
  }

  canEdit(po: AccountPurchaseOrder) {
    return !this.isReadOnly();
  }

  override statusMessage() {
    if (this.isReadOnly()) {
      return 'Account is ' + this.account?.activeStatus.name + ' and is Not Editable';
    }
    return '';
  }

  override isAddDisabled(): boolean {
    return this.isReadOnly();
  }

  async onAddPo(isParent: boolean) {
    this.isAddingParent = isParent;
    const hadAnyParents = this.entities.some((x) => x.isParent());
    await this.onAdd();
    this.parentPos = this.entities.filter((x) => x.parentAccountPurchaseOrderId == x.id);
    this.parentPos.unshift({ id: undefined as any as string, purchaseOrder: '- None -' } as AccountPurchaseOrder);

    this.updateColVisibility();
    this.refreshDisplayedEntities();
    if (!hadAnyParents) {
      AgFns.sizeColumnsToFit(this.gridOptions);
    }
  }

  override async createEntity() {
    const id = Guid.create().toString();
    return this.dbSaveService.createEntity(AccountPurchaseOrder, {
      id: id,
      parentAccountPurchaseOrderId: this.isAddingParent ? id : undefined,
      accountId: this.accountId,
      startDate: DateFns.startOfDay(new Date()),
      endDate: DateFns.startOfDay(DateFns.dateAdd(new Date(), 1, 'year')),
      isBlanketPurchaseOrder: true,
      activeStatusId: ActiveStatusEnum.Active,
    });
  }

  override async createDefaultEntity(entity: AccountPurchaseOrder): Promise<AccountPurchaseOrder | undefined> {
    return undefined;
  }

  override getSortValue(e: AccountPurchaseOrder) {
    return e.purchaseOrder;
  }

  override getAltKeyValue(e: AccountPurchaseOrder) {
    return e.purchaseOrder;
  }

  override async checkIfInUse(po: AccountPurchaseOrder) {
    const isInUse = await this.dbQueryService.checkIfInUse(po, Program, 'accountPurchaseOrderId');
    return isInUse;
  }

  override async onDelete(po: AccountPurchaseOrder) {
    const parent = po.parentAccountPurchaseOrder;
    if (parent != null && parent.id != po.id) {
      const isParentInUse = await this.dbQueryService.checkIfInUse(parent, Program, 'accountPurchaseOrderId');
      if (isParentInUse) {
        this.toastr.warning('You cannot delete a Purchase Order whose parent is in use.', 'Cannot Delete');
        return false;
      }
    }
    const result = await super.onDelete(po);
    this.refreshDisplayedEntities();
    return result;
  }

  override async afterUndo() {
    super.afterUndo();
    this.entities.forEach((e) => e.entityAspect.clearValidationErrors());
    this.parentPos = this.entities.filter((x) => x.parentAccountPurchaseOrderId == x.id);
    this.parentPos.unshift({ id: undefined as any as string, purchaseOrder: '- None -' } as AccountPurchaseOrder);
    this.updateColVisibility();
    this.refreshDisplayedEntities();
    this.gridOptions.apiX?.redrawRows();
    AgFns.sizeColumnsToFit(this.gridOptions);
  }
}
